import { zodResolver } from "@hookform/resolvers/zod";
import EditIcon from "@mui/icons-material/Edit";
import EditOffIcon from "@mui/icons-material/EditOff";
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  CircularProgress,
  Grid2,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import { useFormStyles } from "../../hooks/useFormStyles";
import { FlowTestsInputDataForm, getFlowTestsInputDataFormSchema } from "../../types/forms/flowTest";
import { ConstructionDetails } from "../../types/model/constructionDetails";
import FlowTestsTable from "./FlowTestsTable";

interface CaptureFlowTestsProps {
  captureId: number;
  constructionDetails: ConstructionDetails | undefined;
  canEdit: boolean;
  onSubmit: (values: FlowTestsInputDataForm) => Promise<void>;
}

export default function CaptureFlowTests({
  captureId,
  constructionDetails,
  canEdit,
  onSubmit,
}: Readonly<CaptureFlowTestsProps>) {
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const formStyles = useFormStyles(isEditing);

  const validationSchema = getFlowTestsInputDataFormSchema(t);

  const methods = useForm({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      constructionDetails: {
        drainCasingRadius: constructionDetails?.drainCasingRadius ?? null,
        sandBedHeight: constructionDetails?.sandBedHeight ?? null,
        entubationRadius: constructionDetails?.entubationRadius ?? null,
        boreholeRadius: constructionDetails?.boreholeRadius ?? null,
        firstDrainDepth: constructionDetails?.firstDrainDepth ?? null,
        lastDrainDepth: constructionDetails?.lastDrainDepth ?? null,
      },
    },
  });

  const handleFormSubmit = async (values: FlowTestsInputDataForm) => {
    try {
      setIsSaving(true);
      await onSubmit(values);
      setIsEditing(false);
    } catch (error) {
      handleError(error);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    methods.reset({
      constructionDetails: { ...constructionDetails },
    });
  }, [constructionDetails]);

  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <Grid2 container spacing={2}>
          <Grid2 sx={{ xs: 12 }}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
                width: "100%",
              }}
            >
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 3,
                  }}
                >
                  <Typography variant="h5" sx={{ mb: 2 }}>
                    {t("captures.capture.flow-test.input-data")}
                  </Typography>

                  {/* Action Icons */}
                  {isEditing && canEdit ? ( 
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Tooltip title={t("generic.save")}>
                        <IconButton
                          color="success"
                          onClick={methods.handleSubmit(handleFormSubmit)}
                          disabled={isSaving}
                        >
                          {isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                        </IconButton>
                      </Tooltip>

                      <Tooltip title={t("generic.cancel")}>
                        <IconButton
                          color="error"
                          onClick={() => {
                            setIsEditing(false);
                            methods.reset();
                          }}
                        >
                          <EditOffIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    canEdit && (
                      <Tooltip title={t("generic.edit")}>
                        <IconButton
                          color="primary"
                          onClick={() => setIsEditing(true)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    )
                  )}
                </Box>

                <Grid2 container spacing={2} mt={1}>
                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      label={t(
                        "captures.capture.general.construction-details.drain-casing-radius"
                      )}
                      variant={isEditing ? "outlined" : "standard"}
                      disabled={!isEditing || isSaving}
                      sx={{ ...formStyles }}
                      type="number"
                      {...methods.register("constructionDetails.drainCasingRadius")}
                      value={methods.watch("constructionDetails.drainCasingRadius") ?? ""}
                      slotProps={{
                        input: {
                          endAdornment: <InputAdornment position="end">{t("generic.units.mm")}</InputAdornment>,
                        },
                      }}
                      error={!!methods.formState.errors.constructionDetails?.drainCasingRadius}
                      helperText={methods.formState.errors.constructionDetails?.drainCasingRadius?.message}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      label={t(
                        "captures.capture.general.construction-details.drain-height"
                      )}
                      variant="standard"
                      disabled
                      sx={{ ...formStyles }}
                      value={(() => {
                        const first = methods.watch("constructionDetails.firstDrainDepth") as number | null;
                        const last = methods.watch("constructionDetails.lastDrainDepth") as number | null;

                        if (first == null || last == null) return "";
                        const diff = last - first;
                        return Number.isNaN(diff) ? "" : diff.toFixed(2);
                      })()}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              {t("generic.units.m")}
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      label={t(
                        "captures.capture.general.construction-details.sand-bed-height"
                      )}
                      variant={isEditing ? "outlined" : "standard"}
                      disabled={!isEditing || isSaving}
                      sx={{ ...formStyles }}
                      type="number"
                      {...methods.register("constructionDetails.sandBedHeight")}
                      value={methods.watch("constructionDetails.sandBedHeight") ?? ""}
                      slotProps={{
                        input: {
                          endAdornment: <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>,
                        },
                      }}
                      error={!!methods.formState.errors.constructionDetails?.sandBedHeight}
                      helperText={methods.formState.errors.constructionDetails?.sandBedHeight?.message}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label={t(
                        "captures.capture.general.construction-details.entubation-radius"
                      )}
                      variant={isEditing ? "outlined" : "standard"}
                      disabled={!isEditing || isSaving}
                      sx={{ ...formStyles }}
                      type="number"
                      {...methods.register("constructionDetails.entubationRadius")}
                      value={methods.watch("constructionDetails.entubationRadius") ?? ""}
                      slotProps={{
                        input: {
                          endAdornment: <InputAdornment position="end">{t("generic.units.mm")}</InputAdornment>,
                        },
                      }}
                      error={!!methods.formState.errors.constructionDetails?.entubationRadius}
                      helperText={methods.formState.errors.constructionDetails?.entubationRadius?.message}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label={t(
                        "captures.capture.general.construction-details.borehole-radius"
                      )}
                      variant={isEditing ? "outlined" : "standard"}
                      disabled={!isEditing || isSaving}
                      sx={{ ...formStyles }}
                      type="number"
                      {...methods.register("constructionDetails.boreholeRadius")}
                      value={methods.watch("constructionDetails.boreholeRadius") ?? ""}
                      slotProps={{
                        input: {
                          endAdornment: <InputAdornment position="end">{t("generic.units.mm")}</InputAdornment>,
                        },
                      }}
                      error={!!methods.formState.errors.constructionDetails?.boreholeRadius}
                      helperText={methods.formState.errors.constructionDetails?.boreholeRadius?.message}
                    />
                  </Grid2>
                </Grid2>
              </Box>
            </Paper>
          </Grid2>
        </Grid2>

        <Box mt={5}>
          <FlowTestsTable captureId={captureId} canEdit={canEdit} />
        </Box>
      </Box>
    </FormProvider>
  );
}
